"use client";
import { ButtonSize, ButtonVariant, RegularButton } from "@/stories/button/button";
import React, { useEffect, useState } from "react";
import Quote from "@/app/assets/images/testimonial_quote.png";
import Image from "next/image";
import { IconButton } from "@/stories/icon-button/icon-button";
import ArrowLeft from "@/app/assets/icons/arrow-left";
import ArrowRight from "@/app/assets/icons/arrow-right";
import { TESTIMONIALS } from "@/app/utils/constants";
import useEmblaCarousel from "embla-carousel-react";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import Apple from "@/app/assets/icons/apple";
import GooglePlay from "../assets/icons/googleplay";
import { initBranch, trackEvent } from "../utils/branchio";
import { useAuth } from "@/context/authcontext";
import { setRedirectTo } from "../utils/helper";
import { cn } from "@/lib/utils";

interface TestimonialProps {
    redirectTo?: string | undefined;
    toggleDownloadApp?: (arg0: string) => void;
    btnName?: string;
    eventName?: string;
    sectionClassNames?: string;
}

const Testimonial: React.FC<TestimonialProps> = ({
    redirectTo,
    toggleDownloadApp,
    btnName,
    eventName,
    sectionClassNames,
}) => {
    const { isAuthenticated } = useAuth();
    const [emblaRef, emblaApi] = useEmblaCarousel({
        align: "center",
        containScroll: "trimSnaps",
    });

    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    // Update button states based on Embla API events
    useEffect(() => {
        if (!emblaApi) return;

        const updateScrollButtons = () => {
            setCanScrollPrev(emblaApi.canScrollPrev());
            setCanScrollNext(emblaApi.canScrollNext());
        };

        emblaApi.on("select", updateScrollButtons);
        emblaApi.on("reInit", updateScrollButtons);

        // Initial state
        updateScrollButtons();

        return () => {
            emblaApi.off("select", updateScrollButtons);
            emblaApi.off("reInit", updateScrollButtons);
        };
    }, [emblaApi]);

    const renderIconButton = (
        id: string,
        icon: React.ReactElement,
        action: () => void,
        disabled: boolean,
        additionalClasses: string = "",
    ) => (
        <IconButton
            id={id}
            icon={icon}
            size={ButtonSize.Large}
            variant={ButtonVariant.SecondaryDefault}
            className={`${disabled ? "pointer-events-none opacity-50" : ""} ${additionalClasses}`}
            onClick={action}
            disabled={disabled}
        />
    );

    return (
        <div
            id="section-testimonial"
            className={cn(
                "flex flex-col items-center justify-center pt-s10 md:px-0 md:pb-s20 md:pt-12",
                sectionClassNames,
            )}
        >
            <h2
                id="section-title"
                className="text-center text-3xl font-semibold leading-[130%] tracking-[-0.24px] md:text-6xl md:leading-[120%] md:tracking-[-0.48px]"
            >
                Trusted by 1,50,000+ customers
            </h2>

            <div id="slide-container" className="flex items-center justify-center md:w-full">
                {renderIconButton(
                    "desktop-arrow-left",
                    <ArrowLeft className="text-black" />,
                    emblaApi ? emblaApi.scrollPrev : () => {},
                    !canScrollPrev,
                    "max-md:hidden mr-7 absolute left-20",
                )}

                <div id="slider" className="flex-2 flex h-auto w-auto flex-col items-center md:w-[70%]">
                    <div className="mb-5 mt-9 h-[21.56px] w-[105px]">
                        <Image
                            id="img-quote"
                            src={Quote}
                            alt="Quote Image"
                            className="max-w-full"
                        />
                    </div>

                    <div id="embla-block" className="embla relative mx-auto">
                        <div id="embla-viewport" className="embla__viewport overflow-hidden" ref={emblaRef}>
                            <div id="embla-container" className="embla__container flex">
                                {TESTIMONIALS.map((slide, index: number) => (
                                    <div id={"embla-slide-" + (index+1)} key={index} className="embla__slide flex-[0_0_100%]">
                                        <div className="embla__slide__number">
                                            <div
                                                key={index}
                                                className="flex flex-col items-center justify-center"
                                            >
                                                <p id="statement" className="mb-[18px] text-center text-base font-regular leading-[25.6px] md:text-3xl md:leading-[39.84px]">
                                                    {`${slide.said}`}
                                                </p>
                                                <p id="statement-by" className="text-base font-semibold uppercase text-neutral-300">
                                                    {slide.by}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
                {renderIconButton(
                    "desktop-arrow-right",
                    <ArrowRight className="text-black" />,
                    emblaApi ? emblaApi.scrollNext : () => {},
                    !canScrollNext,
                    "max-md:hidden ml-7 absolute right-20",
                )}
            </div>

            <div className="mb-8 mt-5 flex space-x-4 md:hidden">
                {renderIconButton(
                    "mobile-arrow-left",
                    <ArrowLeft className="text-black" />,
                    emblaApi ? emblaApi.scrollPrev : () => {},
                    !canScrollPrev,
                    "md:hidden",
                )}
                {renderIconButton(
                    "mobile-arrow-right",
                    <ArrowRight className="text-black" />,
                    emblaApi ? emblaApi.scrollNext : () => {},
                    !canScrollNext,
                    "md:hidden",
                )}
            </div>

            {redirectTo && (
                <div className="flex w-full flex-col items-center justify-center md:flex-row">
                    <RegularButton
                        id={
                            btnName
                                ? btnName.toLowerCase().replace(/\s+/gi, "-")
                                : "start-investing"
                        }
                        noIcon
                        showRightIcon={false}
                        size={ButtonSize.Large}
                        variant={ButtonVariant.SecondaryDefault}
                        className="mb-s15 w-full max-w-full bg-white md:mb-0 md:mt-s17 md:max-w-fit"
                        label={btnName}
                        onClick={() => {
                            setAnalyticsEvent(redirectTo, eventName as string);
                            initBranch()
                                .then(() => {
                                    trackEvent(eventName as string, "", "");
                                })
                                .catch((error) => {
                                    console.error("Error initializing Branch:", error);
                                });

                            switch (redirectTo) {
                                case "/refer":
                                    if (redirectTo === "/refer" && isAuthenticated) {
                                        window.location.href = redirectTo;
                                    } else {
                                        setRedirectTo("/refer");
                                        window.location.href = "/login";
                                    }
                                    break;
                                default:
                                    window.location.href = redirectTo;
                            }
                        }}
                    />
                </div>
            )}
            {toggleDownloadApp && (
                <RegularButton
                    id="download-app"
                    noIcon
                    showRightIcon={true}
                    rightIcon={
                        <div className="flex h-12 min-w-16 items-center justify-center gap-s4">
                            <Apple className="h-10 w-10 text-white" />
                            <div className="h-full w-[3px] bg-neutralOpacityDark-20"></div>
                            <GooglePlay className="h-10 w-10 text-white" />
                        </div>
                    }
                    size={ButtonSize.Large}
                    variant={ButtonVariant.PrimaryDefault}
                    label="Download App"
                    className="mb-2 mt-s9 w-full max-w-full md:mb-0 md:mt-[62px] md:max-w-fit"
                    onClick={() => toggleDownloadApp("FD_Download_App_Testimonial")}
                />
            )}
        </div>
    );
};

export default Testimonial;
